/**
 * Post-sign-in entry routing (SPEC-007).
 *
 * Every sign-in lands on the Launch Pad (/users/pad). It used to be a mandatory
 * product chooser; it now resolves the destination from the user's effective
 * access and only shows the launcher when there is a real choice between
 * separate applications. Everything in this module is pure so the rules can be
 * unit-tested; pages/users/pad.jsx supplies the fetched inputs and performs the
 * redirect. The sessionStorage helpers at the bottom carry the entry marker and
 * a requested page across the sign-in round trip (they survive the Cognito
 * hosted-UI redirect because sessionStorage is per tab and per origin).
 *
 * Rules, in order:
 *   1. A requested page or product (bookmark, deep link, SSO `state`) wins —
 *      the user signs in if needed and returns there when authorised.
 *   2. Access to a Hub workspace (Passage, Schedule, Administration) opens the
 *      preferred starting view (last_path) or Hub Home.
 *   3. Exactly one separate product and no Hub workspace opens that product.
 *   4. Several separate products and no Hub workspace show the launcher.
 *   5. No destination at all shows a message with a support route.
 *
 * Hub access (SPEC-012): the backend decides whether an account may enter the
 * Hub at all (`/api/hub/access` → `hub_access.allowed`). When it says no, the
 * Passage and Schedule workspaces are closed here as well, a Hub destination
 * that was asked for is explained with the `hub` notice ("Your account does
 * not have access to OMCi Hub…"), and rule 5 shows that message instead of the
 * generic empty state. Unknown (older backend, failed fetch) counts as
 * allowed — the backend refuses the requests regardless.
 */

// The destinations the portal can route to. `hub: true` products render inside
// the Hub (portal pages); the others are separate applications with their own
// shell and sign-in. Keys match the backend catalog (config/hub_products.yml)
// so the launcher, the product switcher widget and the SSO `state` param all
// speak the same names. `scope` is the legacy user_scope permission that
// grants a separate product when the backend's product list is unavailable.
// Tile colours = the agreed OMCi product palette (SCHED-673), the same values as
// the backend catalog's badge colours and the favicons — keep the three in step.
// `acronym` = the catalog's badge initials, shown on the launcher tile exactly as
// the product switcher shows them (white initials on the product colour).
export const PRODUCTS = {
    dukc: { key: "dukc", label: "DUKC 6", tile: "DUKC 6", acronym: "D6", hub: true, color: "#009584", path: "/users/dashboard#plan" },
    schedule: { key: "schedule", label: "Schedule Optimiser", tile: "SCHEDULE", acronym: "SO", hub: true, color: "#6366F1", path: "/users/dashboard#schedule" },
    dukc5: {
        key: "dukc5",
        label: "DUKC 5",
        tile: "DUKC 5",
        acronym: "D5",
        hub: false,
        scope: "DUKC",
        color: "#009584",
        loginPath: "Security/LogOnWithAWS",
        analytics: "dukc_legacy",
    },
    port: {
        key: "port",
        label: "PortWeather",
        tile: "PORTWEATHER",
        acronym: "PW",
        hub: false,
        scope: "PortWeather",
        color: "#05C0E3",
        loginPath: "login/generic_oauth",
        analytics: "port_weather",
    },
    transit: {
        key: "transit",
        label: "TransitAnalyst",
        tile: "TRANSITANALYST",
        acronym: "TA",
        hub: false,
        scope: "TransitAnalyst",
        color: "#B81D88",
        loginPath: "login",
        analytics: "transit_analyst",
    },
    berth: {
        key: "berth",
        label: "BerthAlert",
        tile: "BERTHALERT",
        acronym: "BA",
        hub: false,
        scope: "BerthAlert",
        color: "#E26B2A",
        loginPath: "login/generic_oauth",
        analytics: "berth_alert",
    },
    keel: {
        key: "keel",
        label: "KeelCheck",
        tile: "KEELCHECK",
        acronym: "KC",
        hub: false,
        scope: "KeelCheck",
        color: "#76BE43",
        loginPath: "Security/LogOnWithAWS",
        analytics: "keel_check",
    },
};

// Launcher order: the DUKC versions side by side (D6, D5), then Schedule, then
// the other separate applications in the backend catalog's order.
export const PRODUCT_ORDER = ["dukc", "dukc5", "schedule", "port", "transit", "berth", "keel"];

export const HUB_HOME = "/users/dashboard";
export const ADMIN_HOME = "/admin/layouts";
export const LAUNCHER_PATH = "/users/pad";

// Next.js only inlines NEXT_PUBLIC_* values that are referenced literally, so
// the separate products' hosts cannot be looked up by a computed name.
function productBaseUrl(key) {
    switch (key) {
        case "dukc5":
            return process.env.NEXT_PUBLIC_DUKC_PATH;
        case "port":
            return process.env.NEXT_PUBLIC_WEATHER_PATH;
        case "transit":
            return process.env.NEXT_PUBLIC_TRANSIT_PATH;
        case "berth":
            return process.env.NEXT_PUBLIC_BERTH_PATH;
        case "keel":
            return process.env.NEXT_PUBLIC_KEEL_PATH;
        default:
            return undefined;
    }
}

// Only the port identifies "where the user was" for a Hub shortcut; page state
// such as `tab` or a plan hash must not leak from the remembered path into a
// tile that promises Passage or Schedule.
export function withRememberedPort(path, lastPath) {
    if (!isValidLastPath(lastPath) || !lastPath.includes("?")) return path;
    const remembered = new URL(lastPath, "http://omc.local");
    const target = new URL(path, "http://omc.local");
    for (const key of ["port", "portId"]) {
        const value = remembered.searchParams.get(key);
        if (value) target.searchParams.set(key, value);
    }
    return target.pathname + target.search + target.hash;
}

/**
 * Absolute link for a destination, or null when the product's host is not
 * configured (a tile is never rendered as a dead link).
 */
export function productHref(key, { lastPath } = {}) {
    const product = PRODUCTS[key];
    if (!product) return null;
    if (product.hub) return withRememberedPort(product.path, lastPath);
    const base = productBaseUrl(key);
    if (!base || typeof base !== "string" || !base.trim()) return null;
    return base.trim().replace(/\/+$/, "") + "/" + product.loginPath;
}

/**
 * The user's effective access, in the shape the routing rules read.
 *
 * @param {object} input
 * @param {string[]} input.scopePermissions  GET /api/user_permissions
 * @param {boolean} input.hasPortalLayout    GET /api/users/show_portal_button → show
 * @param {boolean} input.isAdmin            portal administrator (`context` cookie)
 * @param {string[]} [input.products]        GET /api/hub/access → products, the keys open
 *        to the user (legacy scopes or, where Wider Access is enforced, group
 *        capabilities, per port). Falls back to scopePermissions when missing.
 * @param {boolean} [input.hubAllowed]       GET /api/hub/access → hub_access.allowed
 *        (SPEC-012). Only an explicit `false` closes the Hub.
 */
export function effectiveAccess({ scopePermissions = [], hasPortalLayout = false, isAdmin = false, products = null, hubAllowed } = {}) {
    const scopes = Array.isArray(scopePermissions) ? scopePermissions : [];
    const open = (key) => (Array.isArray(products) ? products.includes(key) : !PRODUCTS[key].scope || scopes.includes(PRODUCTS[key].scope));
    const allowed = hubAllowed !== false;
    const hub = {
        // Same gate the DUKC 6 / Schedule tiles used: a published layout for one
        // of the user's ports, the product open to the user — and, since
        // SPEC-012, the backend's verdict that the account may enter the Hub.
        passage: Boolean(hasPortalLayout) && open("dukc") && allowed,
        schedule: Boolean(hasPortalLayout) && open("schedule") && allowed,
        admin: Boolean(isAdmin),
        allowed,
    };
    const openProducts = PRODUCT_ORDER.filter((key) => !PRODUCTS[key].hub && open(key) && productHref(key) != null);
    return { hub, products: openProducts, hasHub: hub.passage || hub.schedule || hub.admin };
}

/** The `hub` notice when the backend refused the account the Hub, else null. */
export function hubDeniedNotice(access) {
    return access?.hub?.allowed === false ? { type: "hub" } : null;
}

export function isProductAuthorised(access, key) {
    if (key === "admin") return Boolean(access?.hub?.admin);
    const product = PRODUCTS[key];
    if (!product) return false;
    if (product.hub) return key === "dukc" ? Boolean(access?.hub?.passage) : Boolean(access?.hub?.schedule);
    return (access?.products || []).includes(key);
}

// Coarse client-side check for a requested internal page. Pages enforce their
// own access in detail; this only decides whether landing there could make
// sense at all (a PortWeather-only user has no dashboard to return to).
export function isPathAuthorised(access, path) {
    if (typeof path !== "string") return false;
    if (path.startsWith("/admin")) return Boolean(access?.hub?.admin);
    if (path.startsWith("/users/dashboard")) return Boolean(access?.hub?.passage || access?.hub?.schedule);
    return true;
}

const PLACEHOLDER_VALUES = new Set(["", "null", "undefined"]);

// Transient pages are never a destination: a remembered /users/terms would
// loop an accepted user, /login_callback and the launcher itself are hops.
export function isValidLastPath(path) {
    if (typeof path !== "string" || PLACEHOLDER_VALUES.has(path)) return false;
    if (!path.startsWith("/users/")) return false;
    if (path.startsWith("/users/terms") || path.startsWith(LAUNCHER_PATH)) return false;
    return true;
}

export function isValidAdminPath(path) {
    return typeof path === "string" && path.startsWith("/admin") && !PLACEHOLDER_VALUES.has(path);
}

// On a fresh sign-in the user chooses their port again: keep the view, drop
// the remembered port so the dashboard shows the port list (single-port
// accounts load their port regardless).
export function stripPort(path) {
    if (typeof path !== "string" || !path.includes("?")) return path;
    const url = new URL(path, "http://omc.local");
    url.searchParams.delete("port");
    url.searchParams.delete("portId");
    return url.pathname + url.search + url.hash;
}

// Rule 2: the preferred starting view, or Hub Home.
export function hubHome(access, { lastPath, lastAdminPath, freshLogin = false } = {}) {
    if (access?.hub?.passage || access?.hub?.schedule) {
        if (!isValidLastPath(lastPath)) return HUB_HOME;
        return freshLogin ? stripPort(lastPath) : lastPath;
    }
    if (access?.hub?.admin) return isValidAdminPath(lastAdminPath) ? lastAdminPath : ADMIN_HOME;
    return null;
}

/**
 * Only same-origin paths inside the signed-in areas are remembered across a
 * sign-in: protocol-relative, backslash and absolute URLs are refused, as are
 * the transient pages.
 */
export function sanitizeReturnPath(raw) {
    if (typeof raw !== "string") return null;
    const candidate = raw.trim();
    if (!candidate.startsWith("/") || candidate.startsWith("//") || /[\\\s -]/.test(candidate)) return null;
    let url;
    try {
        url = new URL(candidate, "http://omc.local");
    } catch {
        return null;
    }
    if (url.origin !== "http://omc.local") return null;
    const pathname = url.pathname;
    if (!(pathname.startsWith("/users/") || pathname.startsWith("/admin"))) return null;
    if (pathname === LAUNCHER_PATH || pathname.startsWith("/users/terms")) return null;
    return pathname + url.search + url.hash;
}

// The backend puts the product key into the OAuth `state` when an external host
// (PortWeather, …) sends a user into the portal through silent SSO
// (HubMenuService#with_state). "hub" and this portal's own literal "state" mean
// a plain entry.
const SSO_STATE_PRODUCTS = new Set(["dukc", "schedule", "admin"]);

export function mapSsoState(state) {
    const value = Array.isArray(state) ? state[0] : state;
    if (typeof value !== "string" || !SSO_STATE_PRODUCTS.has(value)) return null;
    return { product: value };
}

// Explanation shown on the launcher when a request could not be honoured.
function notice(type, extra = {}) {
    return { type, ...extra };
}

/**
 * Resolve where an entry visit goes.
 *
 * @param {object} input
 * @param {ReturnType<typeof effectiveAccess>} input.access
 * @param {{ product?: string, path?: string } | null} [input.requested]
 * @param {string} [input.lastPath]
 * @param {string} [input.lastAdminPath]
 * @param {boolean} [input.freshLogin]
 * @returns {{ kind: "redirect", href: string, external: boolean, product?: string }
 *         | { kind: "launcher", notice?: object }
 *         | { kind: "none" }}
 */
export function resolveEntry({ access, requested = null, lastPath, lastAdminPath, freshLogin = false }) {
    const opts = { lastPath, lastAdminPath, freshLogin };

    if (requested && requested.product) {
        const key = requested.product;
        if (key === "admin") {
            if (access.hub.admin) return { kind: "redirect", href: hubHome({ hub: { admin: true } }, opts), external: false, product: key };
            return { kind: "launcher", notice: notice("admin") };
        }
        const product = PRODUCTS[key];
        if (!product) return { kind: "launcher", notice: notice("unknown", { product: key }) };
        if (isProductAuthorised(access, key)) {
            const href = productHref(key, opts);
            if (href) return { kind: "redirect", href, external: !product.hub, product: key };
        }
        // A Hub product refused because the account may not enter the Hub at
        // all gets the Hub message; a Hub user lacking this one product keeps
        // the product message.
        if (product.hub && hubDeniedNotice(access)) return { kind: "launcher", notice: hubDeniedNotice(access) };
        return { kind: "launcher", notice: notice("product", { product: product.label }) };
    }

    if (requested && requested.path) {
        const path = sanitizeReturnPath(requested.path);
        if (path && isPathAuthorised(access, path)) return { kind: "redirect", href: path, external: false };
        if (path && path.startsWith("/admin")) return { kind: "launcher", notice: notice("admin") };
        if (path) return { kind: "launcher", notice: hubDeniedNotice(access) || notice("page") };
    }

    if (access.hasHub) return { kind: "redirect", href: hubHome(access, opts), external: false };

    if (access.products.length === 1) {
        const key = access.products[0];
        return { kind: "redirect", href: productHref(key), external: true, product: key };
    }
    if (access.products.length > 1) return { kind: "launcher" };
    // Rule 5. For an account the backend keeps out of the Hub, that refusal is
    // the whole story (SPEC-012), not a generic "no products yet".
    const hubNotice = hubDeniedNotice(access);
    return hubNotice ? { kind: "none", notice: hubNotice } : { kind: "none" };
}

/** Tiles for the launcher: authorised destinations only, in catalog order. */
export function launcherTiles(access, { lastPath } = {}) {
    return PRODUCT_ORDER.filter((key) => isProductAuthorised(access, key))
        .map((key) => {
            const product = PRODUCTS[key];
            const href = productHref(key, { lastPath });
            if (!href) return null;
            return { key, label: product.label, tile: product.tile, acronym: product.acronym, color: product.color, href, external: !product.hub, analytics: product.analytics };
        })
        .filter(Boolean);
}

// --- sign-in round trip state (browser only) ---------------------------------

const RETURN_KEY = "omci_return_to";
const ENTRY_KEY = "omci_entry";
const LEGACY_FRESH_KEY = "fresh_login";

function storage() {
    try {
        return typeof window !== "undefined" ? window.sessionStorage : null;
    } catch {
        return null;
    }
}

/** Remember the page a signed-out visit asked for; returns the stored path or null. */
export function rememberReturnPath(raw) {
    const path = sanitizeReturnPath(raw);
    const store = storage();
    if (!path || !store) return null;
    try {
        store.setItem(RETURN_KEY, path);
    } catch {
        /* storage full or blocked — the user simply lands on their starting view */
    }
    return path;
}

export function consumeReturnPath() {
    const store = storage();
    if (!store) return null;
    try {
        const value = store.getItem(RETURN_KEY);
        store.removeItem(RETURN_KEY);
        return sanitizeReturnPath(value);
    } catch {
        return null;
    }
}

/**
 * Flag the next Launch Pad visit as the post-sign-in entry (auto-routed) rather
 * than an explicit visit (launcher shown). `fresh` means credentials were just
 * entered, so the remembered port is dropped.
 */
export function markEntry({ fresh = false } = {}) {
    const store = storage();
    if (!store) return;
    try {
        store.setItem(ENTRY_KEY, fresh ? "fresh" : "continue");
    } catch {
        /* ignore */
    }
}

/** Mark an entry only when none is pending, so a fresh sign-in keeps its flag. */
export function ensureEntryMarked() {
    if (!peekEntry().entry) markEntry();
}

export function peekEntry() {
    const store = storage();
    if (!store) return { entry: false, fresh: false };
    try {
        const value = store.getItem(ENTRY_KEY);
        if (value) return { entry: true, fresh: value === "fresh" };
        if (store.getItem(LEGACY_FRESH_KEY) != null) return { entry: true, fresh: true };
    } catch {
        /* ignore */
    }
    return { entry: false, fresh: false };
}

export function clearEntry() {
    const store = storage();
    if (!store) return;
    try {
        store.removeItem(ENTRY_KEY);
        store.removeItem(LEGACY_FRESH_KEY);
    } catch {
        /* ignore */
    }
}
